/**
 * Authorization Utilities
 * Centralized authorization and HTTP error handling for Gateway API requests
 *
 * ⚠️ SECURITY: These utilities enforce proper authorization patterns.
 * See /SECURITY_GUIDELINES.md for mandatory security requirements.
 */

import {
  GatewayError,
  GatewayErrorCode,
  isGatewayError,
} from './gateway/error-codes';
import { logger } from './logger';
import { SessionEndedError, hasSessionEnded } from './session-ended';

/**
 * Auth service interface for CSRF token access.
 *
 * `refreshSession()` is optional. When implemented (e.g. by Typo3AuthService),
 * the request handler calls it once after a 403 CSRF rejection to re-fetch a
 * token the backend has rotated under us, then retries the request. A 401 is
 * a lost session and goes to the session-ended notifier instead.
 */
export interface AuthService {
  getCsrfToken(): string | null;
  refreshSession?(): Promise<boolean>;
}

/**
 * Get CSRF token from auth service with error handling
 * @param authService - Auth service instance
 * @returns CSRF token
 * @throws Error if token not available
 */
export function getRequiredCsrfToken(
  authService: AuthService | null | undefined
): string {
  const csrfToken = authService?.getCsrfToken?.();
  if (!csrfToken) {
    logger.error('CSRF token not available - cannot make request');
    throw new GatewayError(
      'CSRF token not available',
      GatewayErrorCode.CSRF_MISSING
    );
  }
  return csrfToken;
}

/** Parse a JSON error body; null for no body, non-JSON or a non-object. */
export async function readErrorBody(
  response: Pick<Response, 'text'> | { text?: undefined }
): Promise<Record<string, unknown> | null> {
  try {
    if (typeof response.text !== 'function') return null;
    const parsed: unknown = JSON.parse(await response.text());
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Turn auth failures into typed errors. Call BEFORE checking response.ok.
 *  - 401 → SessionEndedError (kind/reason from the body; 'expired'/UNKNOWN otherwise)
 *  - 403 CSRF_MISSING/CSRF_INVALID or unreadable → CSRF_REJECTED (refresh + retry may help)
 *  - other 403 codes → FORBIDDEN with details.serverCode (no retry)
 *  - 404 → Error('Resource not found')
 */
export async function assertAuthorized(response: Response): Promise<void> {
  if (response.status === 401) {
    const body = await readErrorBody(response);
    const ended = body?.code === 'SESSION_ENDED';
    throw new SessionEndedError({
      kind: ended && body?.kind === 'access_ended' ? 'access_ended' : 'expired',
      reason:
        ended && typeof body?.reason === 'string' ? body.reason : 'UNKNOWN',
    });
  }
  if (response.status === 403) {
    const body = await readErrorBody(response);
    const serverCode = typeof body?.code === 'string' ? body.code : null;
    if (
      serverCode === null ||
      serverCode === 'CSRF_MISSING' ||
      serverCode === 'CSRF_INVALID'
    ) {
      throw new GatewayError(
        'Request forbidden - CSRF validation failed',
        GatewayErrorCode.CSRF_REJECTED,
        { status: 403, serverCode }
      );
    }
    throw new GatewayError(
      `Request forbidden: ${typeof body?.error === 'string' ? body.error : serverCode}`,
      GatewayErrorCode.FORBIDDEN,
      { status: 403, serverCode }
    );
  }
  if (response.status === 404) {
    throw new Error('Resource not found');
  }
}

/**
 * Build authorization headers for proxy mode
 * @param csrfToken - CSRF token from auth service
 * @param includeContentType - Whether to include Content-Type header (default: true)
 * @returns Headers object for proxy requests
 */
export function buildProxyHeaders(
  csrfToken: string,
  includeContentType: boolean = true
): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-CSRF-Token': csrfToken,
  };
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

/**
 * Build authorization headers for direct mode
 * @param authToken - Bearer token
 * @param includeContentType - Whether to include Content-Type header (default: true)
 * @returns Headers object for direct requests
 */
export function buildDirectHeaders(
  authToken: string,
  includeContentType: boolean = true
): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${authToken}`,
  };
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

/**
 * Run a proxy request; after a 403 CSRF rejection refresh the session once and
 * run it again (#113: 3004 and the upload, as 3002 does in request-handler).
 * `attempt` must read the CSRF token itself, so the retry carries the new one.
 *  - refresh fails because the session ended → stays pending (the dialog shows)
 *  - refresh fails otherwise → the original error
 *  - a second rejection → thrown, no third attempt
 */
export async function withCsrfRetry<T>(
  authService: AuthService | null | undefined,
  attempt: () => Promise<T>
): Promise<T> {
  try {
    return await attempt();
  } catch (error) {
    if (
      !(error instanceof GatewayError) ||
      error.code !== GatewayErrorCode.CSRF_REJECTED ||
      typeof authService?.refreshSession !== 'function'
    ) {
      throw error;
    }
    logger.info(
      'Proxy rejected the CSRF token — refreshing session and retrying once'
    );
    if (await authService.refreshSession()) {
      return attempt();
    }
    if (hasSessionEnded()) return new Promise<T>(() => {});
    logger.warn('Session refresh failed; bubbling CSRF error');
    throw error;
  }
}

/** assertAuthorized turns every 404 into Error('Resource not found'); this names that case. */
export function isNotFound(error: unknown): boolean {
  return (
    error instanceof Error &&
    !isGatewayError(error) &&
    error.message === 'Resource not found'
  );
}
